import React, { useContext, useRef } from 'react';

import { Button, Space, Typography } from '@sravni/react-design-system';
import { PartnerBadge } from '@sravni/react-ugc/lib/PartnerBadge';
import { useStyles } from '@sravni/react-utils';

import { VerticalListContext } from '../../../../contexts/VerticalListQueryContext';
import { schemaToElement } from '../../../../helpers/schemaToElement';
import type { EventPayload } from '../../../../helpers/sendMessageToParentWindow';
import { useOfferData } from '../../../../hooks/useOfferData';
import type { EnrichedOfferFromBff } from '../../../../interfaces/offers';
import { WIDGET_TYPE } from '../../../../types/widgetType';
import { Logo } from '../../../Block/components/HorizontalBlocks/OfferBlock/Logo';
import { useViewObserver } from '../../../Block/hooks/useViewObserver';

import styles from './block.module.scss';

export const Block = ({
  offer,
  isMobile,
  handleClickBlock,
  handleViewBlock,
}: {
  offer: EnrichedOfferFromBff;
  isMobile: boolean;
  handleClickBlock: (event: EventPayload) => void;
  handleViewBlock: (event: EventPayload) => void;
}) => {
  const cx = useStyles(styles);
  const ref = useRef<HTMLAnchorElement>();
  const { tab } = useContext(VerticalListContext);
  const conditionsRef = useRef<HTMLDivElement>();

  const {
    bankName,
    logoSrc,
    isCard,
    conditions,
    isPartnerCard,
    noticeAboutAdvertisement,
    clickLink,
    buttonLabel,
    sendViewingEventViaPixel,
    sendClickEventViaPixel,
    clickBlockEvent,
    viewBlockEvent,
  } = useOfferData({ tab, item: offer, isPopup: false, widgetType: WIDGET_TYPE.vertical });

  const handleClick = () => {
    handleClickBlock(clickBlockEvent);
    sendClickEventViaPixel();
  };

  const handleView = () => {
    handleViewBlock(viewBlockEvent);
    sendViewingEventViaPixel();
  };

  useViewObserver({
    handleView,
    blockRef: ref,
  });

  const logo = <Logo logoSrc={logoSrc} isCard={isCard} tabType={tab} size={isMobile ? 44 : 60} />;
  const logoBlock = isPartnerCard ? <PartnerBadge>{logo}</PartnerBadge> : logo;

  return (
    <a className={cx('wrapper')} href={clickLink} onClick={handleClick} target="_blank" rel="noreferrer" ref={ref}>
      <Space
        direction={isMobile ? 'vertical' : 'horizontal'}
        align="center"
        size={isMobile ? 16 : 16}
        justify="space-between"
        className={cx('content')}
      >
        <Space direction="horizontal" size={16} align="center" className={cx('org-info')}>
          {logoBlock}
          <div className={cx('org-info__text')}>
            <div className={cx('h-mb-4', 'heading_h5', 'h-color-D100', 'bank-name')}>{bankName}</div>
            {/* <RatingWithReviews ratingValue={ratingValue} reviewsCount={reviewsCount} isMobile={false} />*/}
          </div>
        </Space>
        {conditions && (
          <Space
            direction="horizontal"
            size={isMobile ? 8 : 12}
            justify={isMobile ? 'space-between' : 'end'}
            className={cx('conditions')}
            ref={conditionsRef}
          >
            {conditions.map((condition, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={cx('condition')} key={idx}>
                <div className={cx('h-color-D60', 'description')}>{condition.description}</div>
                <div className={cx('h-mt-4', 'heading_h5', 'h-color-D100')}>
                  {condition.SOValue ? schemaToElement(condition.SOValue) : condition.value}
                </div>
              </div>
            ))}
          </Space>
        )}
      </Space>
      <Button size={52} variant="primary" block className={isMobile ? 'h-mt-16' : 'h-mt-24'}>
        {buttonLabel}
      </Button>
      {noticeAboutAdvertisement && (
        <Typography.Text size={8} className="h-color-D60 h-mt-16 h-text-center">
          {noticeAboutAdvertisement}
        </Typography.Text>
      )}
    </a>
  );
};
